import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { formatBRL, formatDate } from '@/pages/painel/lib/comercial';
import {
  FilaItem,
  scoreTone,
  STATUS_COMERCIAL,
  DECISOES_OVERRIDE,
  DECISOES_OVERRIDE_LABELS,
} from '@/pages/painel/lib/inteligencia';

interface FilaCardProps {
  item: FilaItem;
  password: string;
  onChanged: () => void;
}

type ActionPanel = 'acao' | 'status' | 'override' | null;

export default function FilaCard({ item, password, onChanged }: FilaCardProps) {
  const [panel, setPanel] = useState<ActionPanel>(null);
  const [showScore, setShowScore] = useState(false);
  const [acao, setAcao] = useState('');
  const [acaoData, setAcaoData] = useState('');
  const [status, setStatus] = useState('');
  const [decisao, setDecisao] = useState('nao_prospectar');
  const [motivoOverride, setMotivoOverride] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const tone = scoreTone(item.score ?? 0);

  const abrirUrl = (() => {
    if (item.empresa_id) return `/painel/clientes/${item.empresa_id}`;
    return null;
  })();

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setPanel(null);
      onChanged();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Erro ao executar ação.');
    } finally {
      setBusy(false);
    }
  };

  const salvarAcao = () =>
    run(async () => {
      const { data, error } = await supabase.functions.invoke('filas-admin', {
        body: {
          action: 'fila_acao',
          password,
          entidade_tipo: item.entidade_tipo,
          entidade_id: item.entidade_id,
          acao,
          data: acaoData || undefined,
          contato_id: item.contato_id,
          empresa_id: item.empresa_id,
          oportunidade_id: item.oportunidade_id,
        },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      if (data?.error) throw new Error(data.error);
    });

  const salvarStatus = () =>
    run(async () => {
      const { data, error } = await supabase.functions.invoke('filas-admin', {
        body: { action: 'fila_status', password, entidade_tipo: item.entidade_tipo, entidade_id: item.entidade_id, status },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      if (data?.error) throw new Error(data.error);
    });

  const salvarOverride = () =>
    run(async () => {
      const { data, error } = await supabase.functions.invoke('inteligencia-admin', {
        body: {
          action: 'override_salvar',
          password,
          entidade_tipo: item.entidade_tipo,
          entidade_id: item.entidade_id,
          decisao_manual: decisao,
          motivo_decisao_manual: motivoOverride,
        },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      if (data?.error) throw new Error(data.error);
    });

  const renderComponentes = () => {
    if (!item.score_componentes) return <p className="text-xs text-gray-400">Sem detalhamento de score disponível.</p>;
    return (
      <div className="space-y-1">
        {item.score_componentes.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-3 text-xs">
            <span className="text-gray-600">{c.label}</span>
            <span className={`font-semibold whitespace-nowrap ${c.pontos > 0 ? 'text-green-700' : 'text-gray-400'}`}>
              {c.evidencia ? `+${c.pontos}` : '—'}
            </span>
          </div>
        ))}
        {item.score_insuficiente && (
          <p className="text-[11px] text-amber-600 mt-2">Dados insuficientes — score parcial.</p>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 leading-snug">{item.nome}</p>
            {item.tipo && (
              <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {item.tipo}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1 text-xs text-gray-500">
            {item.empresa && item.empresa !== item.nome && <span className="truncate max-w-[260px]">{item.empresa}</span>}
            {item.cidade && <span>{item.cidade}{item.uf ? `/${item.uf}` : ''}</span>}
          </div>
        </div>

        {item.score != null && (
          <button
            onClick={() => setShowScore((s) => !s)}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-lg border cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50"
            title="Por quê? — clique para ver os componentes"
          >
            <span className={tone.tone === 'bg-green-50 text-green-700 border-green-200' ? 'text-green-700' : tone.tone.includes('amber') ? 'text-amber-700' : 'text-gray-600'}>
              {item.score}
            </span>
            <span className="text-gray-400 font-normal">{tone.label}</span>
            <i className="ri-question-line text-gray-400"></i>
          </button>
        )}
      </div>

      {/* Motivo da fila */}
      <div className="mt-2.5 flex items-start gap-2">
        <i className="ri-information-line text-gray-400 mt-0.5"></i>
        <p className="text-xs text-gray-600">{item.motivo}</p>
      </div>

      {/* Meta */}
      <div className="flex items-center gap-3 flex-wrap mt-2 text-xs">
        {item.ultimo_contato && (
          <span className="inline-flex items-center gap-1 text-gray-500">
            <i className="ri-time-line"></i>
            {formatDate(item.ultimo_contato)}
          </span>
        )}
        {item.dias_sem_contato != null && (
          <span className={`font-semibold ${item.dias_sem_contato > 60 ? 'text-amber-600' : 'text-gray-500'}`}>
            {item.dias_sem_contato} dia(s) sem contato
          </span>
        )}
        {item.valor_potencial != null && item.valor_potencial > 0 && (
          <span className="font-semibold text-gray-700">Potencial {formatBRL(item.valor_potencial)}</span>
        )}
        {item.lucro_potencial != null && item.lucro_potencial > 0 && (
          <span className="font-semibold text-green-700">Lucro {formatBRL(item.lucro_potencial)}</span>
        )}
      </div>

      {item.produtos.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          {item.produtos.slice(0, 5).map((p) => (
            <span key={p} className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-600">
              {p}
            </span>
          ))}
        </div>
      )}

      {item.resumo_ia && (
        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{item.resumo_ia}</p>
      )}

      {item.proxima_acao && (
        <div className="mt-2 text-xs bg-gray-50 rounded-lg px-3 py-2">
          <span className="text-gray-500">Próxima ação: </span>
          <span className="text-gray-800 font-medium">{item.proxima_acao}</span>
          {item.proxima_acao_data && <span className="text-gray-400"> · {formatDate(item.proxima_acao_data)}</span>}
        </div>
      )}

      {/* Score componentes */}
      {showScore && item.score != null && (
        <div className="mt-3 bg-gray-50 rounded-lg p-3">
          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Por quê? — componentes do score</p>
          {renderComponentes()}
        </div>
      )}

      {/* Ações */}
      <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-gray-100">
        {abrirUrl ? (
          <Link
            to={abrirUrl}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2.5 py-1.5 rounded-lg hover:bg-[#E8610A]/10 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-up-line"></i>
            Abrir
          </Link>
        ) : (
          item.oportunidade_id && (
            <Link
              to="/painel/oportunidades"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2.5 py-1.5 rounded-lg hover:bg-[#E8610A]/10 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-up-line"></i>
              Oportunidade
            </Link>
          )
        )}

        <button
          onClick={() => setPanel(panel === 'acao' ? null : 'acao')}
          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-800 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-calendar-event-line"></i>
          Próxima ação
        </button>
        <button
          onClick={() => setPanel(panel === 'status' ? null : 'status')}
          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-800 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-flag-line"></i>
          Status
        </button>
        <button
          onClick={() => setPanel(panel === 'override' ? null : 'override')}
          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-800 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-shield-check-line"></i>
          Override
        </button>
      </div>

      {item.override && (
        <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
          <i className="ri-shield-check-line"></i> Override: {DECISOES_OVERRIDE_LABELS[item.override.decisao_manual] ?? item.override.decisao_manual}
          {item.override.motivo ? ` — ${item.override.motivo}` : ''}
        </div>
      )}

      {msg && <p className="mt-2 text-xs text-red-600">{msg}</p>}

      {/* Painel: próxima ação */}
      {panel === 'acao' && (
        <div className="mt-3 bg-gray-50 rounded-lg p-3 space-y-2">
          <input
            value={acao}
            onChange={(e) => setAcao(e.target.value)}
            placeholder="O que fazer? (ex.: ligar e retomar proposta)"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <input
            type="date"
            value={acaoData}
            onChange={(e) => setAcaoData(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <button
            onClick={salvarAcao}
            disabled={busy || !acao.trim()}
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#E8610A] text-white px-3 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
            Transformar em próxima ação
          </button>
        </div>
      )}

      {/* Painel: status */}
      {panel === 'status' && (
        <div className="mt-3 bg-gray-50 rounded-lg p-3 space-y-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:border-[#E8610A]"
          >
            <option value="">Selecionar status...</option>
            {STATUS_COMERCIAL.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <button
            onClick={salvarStatus}
            disabled={busy || !status}
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-gray-800 text-white px-3 py-2 rounded-lg hover:bg-gray-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
            Salvar status
          </button>
        </div>
      )}

      {/* Painel: override */}
      {panel === 'override' && (
        <div className="mt-3 bg-amber-50 border border-amber-100 rounded-lg p-3 space-y-2">
          <p className="text-[11px] text-amber-700">Decisão manual prevalece sobre IA/score/filas.</p>
          <select
            value={decisao}
            onChange={(e) => setDecisao(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:border-[#E8610A]"
          >
            {DECISOES_OVERRIDE.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
          <input
            value={motivoOverride}
            onChange={(e) => setMotivoOverride(e.target.value)}
            placeholder="Motivo (opcional)"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <button
            onClick={salvarOverride}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-shield-check-line"></i>}
            Aplicar override
          </button>
        </div>
      )}
    </div>
  );
}